<template>
  <v-container class="technical-review-dashboard pa-0">
    <v-row>
      <v-col cols="12">
        <h1 class="display-1 font-weight-medium mb-4">
          Revisões Técnicas do Estado
        </h1>
      </v-col>
    </v-row>

    <v-alert v-if="!hasAccess" type="warning" border="left" colored-border>
      Você não possui permissão para acessar o painel de revisões técnicas do
      estado. Solicite a liberação ao administrador do ambiente.
    </v-alert>

    <template v-else>
      <v-card class="mb-6">
        <v-card-text>
          <v-form @submit.prevent="applyFilters">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="filters.municipios"
                  :items="municipioOptions"
                  :loading="loadingMunicipios"
                  :disabled="loadingMunicipios"
                  item-text="nome"
                  item-value="municipioId"
                  label="Sala/Parceiro"
                  multiple
                  clearable
                  chips
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="filters.status"
                  :items="statusOptions"
                  item-text="text"
                  item-value="value"
                  label="Status"
                  multiple
                  clearable
                  chips
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row justify="end" class="mt-2">
              <v-btn text color="primary" class="mr-2" @click="resetFilters">
                Limpar filtros
              </v-btn>
              <v-btn color="primary" @click="applyFilters">Aplicar filtros</v-btn>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>

      <v-data-table
        :headers="tableHeaders"
        :items="displayedReviews"
        :loading="loading"
        :items-per-page="15"
        class="elevation-1"
        :item-class="() => 'technical-review-dashboard__row'"
        @click:row="goToReviewDetail"
      >
        <template v-slot:loading>
          <v-progress-linear indeterminate color="primary"></v-progress-linear>
        </template>

        <template v-slot:item.statusLabel="{ item }">
          <v-chip small :color="item.statusColor" dark>
            {{ item.statusLabel }}
          </v-chip>
        </template>

        <template v-slot:no-data>
          <v-alert type="info" border="left" colored-border>
            Nenhuma revisão técnica encontrada com os filtros selecionados.
          </v-alert>
        </template>

        <template v-slot:footer.page-text="items">
          {{ items.pageStart }} - {{ items.pageStop }} de {{ items.itemsLength }}
        </template>
      </v-data-table>
    </template>
  </v-container>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import { cityAPI } from "@/modules/city";
import { technicalReviewAPI } from "@/modules/technical-review/API/TechnicalReviewAPI";
import { buildCriterionDisplayLabel } from "@/helpers/criteria";
import {
  REVIEW_STATUSES,
  findStatusMeta,
  normalizeReviewStatus,
} from "@/modules/technical-review/constants";

export default {
  name: "TechnicalReviewDashboard",
  data() {
    return {
      loading: false,
      initialized: false,
      reviews: [],
      municipioOptions: [],
      loadingMunicipios: false,
      filters: {
        municipios: [],
        status: [],
      },
    };
  },
  computed: {
    ...mapGetters(["config", "groupsConfig", "isFullAdmin"]),
    hasAccess() {
      if (this.isFullAdmin) {
        return true;
      }
      return (
        (this.config.painelRevisaoTecnicaEstado ?? false) &&
        (this.groupsConfig.painelRevisaoTecnicaEstado ?? false)
      );
    },
    statusOptions() {
      return REVIEW_STATUSES.filter(
        (status) => status.value !== "GESTAO_NACIONAL"
      );
    },
    tableHeaders() {
      return [
        { text: "ID da revisão técnica", value: "seloRevisaoTecnicaId" },
        { text: "Sala/Parceiro", value: "salaParceiroNome" },
        { text: "Critério", value: "criterioDescricao" },
        { text: "Status atual", value: "statusLabel" },
        { text: "Data de envio", value: "dataEnvioFormatada" },
        { text: "Data de conclusão", value: "dataConclusaoFormatada" },
        { text: "Tempo total aberto (dias)", value: "tempoTotalAbertoFormatado" },
        { text: "Tempo de análise (dias)", value: "tempoRespostaFormatado" },
      ];
    },
    displayedReviews() {
      return this.reviews;
    },
  },
  watch: {
    hasAccess(newValue) {
      if (newValue && !this.initialized) {
        this.initialize();
      }
    },
  },
  created() {
    if (this.hasAccess) {
      this.initialize();
    }
    this.loadMunicipios();
  },
  methods: {
    async initialize() {
      this.initialized = true;
      await this.loadReviews();
    },
    resetFilters() {
      this.filters.municipios = [];
      this.filters.status = [];
      this.loadReviews();
    },
    async applyFilters() {
      await this.loadReviews();
    },
    async loadReviews() {
      if (!this.hasAccess) {
        return;
      }
      this.loading = true;
      try {
        const payload = this.buildFilterPayload();
        const { data } = await technicalReviewAPI.stateDashboard(payload);
        const revisoes = Array.isArray(data)
          ? data
          : data?.RevisoesTecnicas || data?.revisoesTecnicas || [];
        this.reviews = revisoes.map((item) => this.normalizeReview(item));
      } catch (error) {
        console.error("Erro ao carregar revisões técnicas do estado", error);
        this.$toast?.error?.(
          "Não foi possível carregar as revisões técnicas do estado"
        );
      } finally {
        this.loading = false;
      }
    },
    async loadMunicipios() {
      this.loadingMunicipios = true;
      try {
        const { data } = await cityAPI.list();
        const municipios = Array.isArray(data) ? data : data?.municipios ?? [];
        this.municipioOptions = municipios
          .map((item) => ({
            municipioId: Number(
              item.municipioId ?? item.MunicipioId ?? item.id ?? item.codigo
            ),
            nome: String(
              item.nome ?? item.Nome ?? item.municipioNome ?? item.name ?? ""
            ).trim(),
          }))
          .filter((item) => !Number.isNaN(item.municipioId) && item.nome.length > 0)
          .sort((a, b) => a.nome.localeCompare(b.nome));
      } catch (error) {
        console.error("Erro ao carregar municípios", error);
        this.$toast?.error?.("Não foi possível carregar as salas/parceiros");
      } finally {
        this.loadingMunicipios = false;
      }
    },
    buildFilterPayload() {
      const municipios = (this.filters.municipios || [])
        .map((item) => Number(item?.municipioId ?? item))
        .filter((value) => !Number.isNaN(value));
      const status = (this.filters.status || []).filter(Boolean);

      return {
        Municipios: municipios,
        RevisoesTecnicasStatus: status,
      };
    },
    normalizeReview(raw) {
      const rawStatus = raw.Status ?? raw.status ?? "";
      const status = normalizeReviewStatus(rawStatus);
      const statusMeta = findStatusMeta(status);

      const seloRevisaoTecnicaId =
        raw.SeloRevisaoTecnicaId ?? raw.seloRevisaoTecnicaId ?? 0;
      const municipioId = raw.MunicipioId ?? raw.municipioId ?? null;
      const municipioNome = raw.MunicipioNome ?? raw.municipioNome ?? "";
      const salaNome = raw.SalaNome ?? raw.salaNome ?? "";
      const criterioTitulo = raw.CriterioTitulo ?? raw.criterioTitulo ?? "";
      const criterioNumero = raw.CriterioNumero ?? raw.criterioNumero ?? null;
      const dataCriacao = raw.DataCriacao ?? raw.dataCriacao ?? null;
      const dataEnvio = raw.DataEnvio ?? raw.dataEnvio ?? null;
      const dataConclusao = raw.DataConclusao ?? raw.dataConclusao ?? null;
      const tempoRespostaHorasUteis =
        raw.TempoRespostaHorasUteis ?? raw.tempoRespostaHorasUteis ?? null;
      const tempoRespostaDias = this.convertHoursToDays(tempoRespostaHorasUteis);
      const tempoTotalAberto = this.calculateTotalOpenTime(
        dataCriacao,
        dataConclusao
      );

      return {
        seloRevisaoTecnicaId,
        municipioId,
        municipioNome,
        salaNome,
        salaParceiroNome: salaNome || municipioNome || "-",
        criterioDescricao: this.buildCriterioLabel(
          criterioNumero,
          criterioTitulo
        ),
        status,
        rawStatus,
        statusLabel: statusMeta.text || status,
        statusColor: statusMeta.color || "primary",
        dataEnvioFormatada: this.formatDateTime(dataEnvio),
        dataConclusaoFormatada: this.formatDateTime(dataConclusao),
        tempoRespostaFormatado: this.formatNumber(tempoRespostaDias, "d"),
        tempoTotalAbertoFormatado: this.formatNumber(tempoTotalAberto, "d"),
      };
    },
    buildCriterioLabel(numero, titulo) {
      const parts = [];
      if (numero !== null && numero !== undefined) {
        const numeroLabel = String(numero).trim();
        if (numeroLabel) {
          parts.push(numeroLabel);
        }
      }

      const tituloLabel = String(titulo || "").trim();
      if (tituloLabel) {
        parts.push(tituloLabel);
      }

      if (!parts.length) {
        return "";
      }

      const combined = parts.join(" - ");
      return buildCriterionDisplayLabel(combined);
    },
    formatDateTime(value) {
      if (!value) {
        return "-";
      }
      return moment(value).format("DD/MM/YYYY HH:mm");
    },
    formatNumber(value, suffix = "") {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "-";
      }
      return `${Number(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      })}${suffix ? ` ${suffix}` : ""}`;
    },
    calculateTotalOpenTime(dataCriacao, dataConclusao) {
      if (!dataCriacao) {
        return null;
      }
      const inicio = moment(dataCriacao);
      const fim = dataConclusao ? moment(dataConclusao) : moment();
      const diffHoras = fim.diff(inicio, "hours", true);
      return diffHoras / 24;
    },
    convertHoursToDays(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return null;
      }
      return Number(value) / 24;
    },
    goToReviewDetail(item) {
      const municipioId = item?.municipioId;
      const revisaoId = item?.seloRevisaoTecnicaId;

      if (!municipioId || !revisaoId) {
        return;
      }

      this.$router.push({
        name: "technical-review-detail",
        params: {
          municipioId,
          revisaoId,
        },
      });
    },
  },
};
</script>

<style scoped>
.technical-review-dashboard {
  max-width: 100%;
}

.technical-review-dashboard__row {
  cursor: pointer;
}
</style>
